import type { RootStore } from '~/stores/root.store';
import {
    CoreOutboundEvents,
    PageInboundEvents,
    PageOutboundEvents
} from '~/components/Builder/Mobile/builderMobileApi.enums';
import {
    IMobileAppService,
    IMobilePage
} from '~/components/Builder/Mobile/builderMobileApi.types';
import { getPagesProperties } from '~/components/Builder/Mobile/builderMobileApi.utils';
import BaseAbstractBuilderApi from '~/components/Builder/Mobile/Entities/BaseAbstractBuilderApi';
import BuilderMobileCoreApiHandler from '~/components/Builder/Mobile/Entities/Core/BuilderMobileCoreApiHandler';
import { FlowMXContextValue } from '~/components/Builder/FlowMX.provider';
import { scrollToBlock } from '~/utils/flow.utils';

export class BuilderMobilePageApi extends BaseAbstractBuilderApi {
    builderMobileCoreApi: BuilderMobileCoreApiHandler;
    constructor(
        rootStore: RootStore,
        flowMXContext: FlowMXContextValue,
        MobileAppService: IMobileAppService,
        builderMobileCoreApi: BuilderMobileCoreApiHandler
    ) {
        super(rootStore, flowMXContext, MobileAppService);
        this.builderMobileCoreApi = builderMobileCoreApi;
        Object.values(PageInboundEvents).forEach(pageInboundEvent => {
            this.subscribeToEvent(pageInboundEvent);
        });
    }

    // INBOUND
    [PageInboundEvents.GetPages]() {
        this[PageOutboundEvents.PagesList]();
    }

    [PageInboundEvents.AddPage]({
        pageTitle,
        afterPageId
    }: {
        pageTitle: string;
        afterPageId: string;
    }) {
        const {
            builderUiStore: { selectedFlow },
            pageCtrl
        } = this.rootStore;

        const { pagesMap } = selectedFlow.draftRevision;

        const afterPage = afterPageId ? pagesMap[afterPageId] : null;

        pageCtrl.createPage(pageTitle, afterPage?.order);

        this.builderMobileCoreApi[CoreOutboundEvents.PageLoaded]();
    }

    [PageInboundEvents.UpdatePage]({ page }: { page: IMobilePage }) {
        const {
            pageCtrl,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const isPageUndergoneReordering = this.isPageReordered(page);
        pageCtrl.updatePage(page.page_id, page);
        if (
            isPageUndergoneReordering &&
            selectedFlow.isContractRecapsBeforeSelection
        ) {
            selectedFlow.removeAllRecapsBeforeSelection();
        }
        this.selectPageAndSendPageLoaded(page.page_id);
    }

    [PageInboundEvents.DeletePage]({ pageId }: { pageId: string }) {
        const {
            builderUiStore: { selectedFlow },
            pageCtrl
        } = this.rootStore;

        const { pagesMap } = selectedFlow.draftRevision;

        const page = pagesMap[pageId];

        pageCtrl.deletePage(page);

        this.builderMobileCoreApi[CoreOutboundEvents.PageLoaded]();
    }

    [PageInboundEvents.DuplicatePage]({ pageId }: { pageId: string }) {
        const {
            builderUiStore: { selectedFlow },
            pageCtrl
        } = this.rootStore;

        const { pagesMap } = selectedFlow.draftRevision;

        const page = pagesMap[pageId];

        pageCtrl.duplicatePage(page);

        this.builderMobileCoreApi[CoreOutboundEvents.PageLoaded]();
    }

    [PageInboundEvents.UpdatePageBackgroundImage]({
        pageRootBlockId,
        img
    }: {
        pageRootBlockId: string;
        img: any;
    }) {
        const { blockCtrl } = this.rootStore;

        blockCtrl.updateBlock(
            { block_id: pageRootBlockId },
            { background_image: img }
        );

        this.builderMobileCoreApi[CoreOutboundEvents.PageLoaded]();
    }

    [PageInboundEvents.SelectPage]({ pageId }: { pageId: string }) {
        this.selectPageAndSendPageLoaded(pageId);
    }
    // OUTBOUND

    [PageOutboundEvents.PagesList]() {
        const {
            builderUiStore: { selectedFlow, selectedPageId }
        } = this.rootStore;

        const pages = getPagesProperties(selectedFlow);
        this.MobileAppService.triggerAppEvent(PageOutboundEvents.PagesList, {
            pages,
            selectedPage: pages.filter(x => x.page_id === selectedPageId)?.[0]
        });
    }

    private selectPageAndSendPageLoaded = (pageId: string): void => {
        const {
            builderUiStore: { selectPage }
        } = this.rootStore;

        selectPage(pageId);
        setTimeout(() => {
            const blocksWithErrorsOnPage =
                this.rootStore.builderUiStore.selectedPage.blocksWithErrors;
            if (blocksWithErrorsOnPage.length > 0) {
                scrollToBlock({
                    blockId: blocksWithErrorsOnPage[0].block_id,
                    isMobileApp: true
                });
            }
        }, 0);

        this.builderMobileCoreApi[CoreOutboundEvents.PageLoaded]();
    };

    private isPageReordered = (page: IMobilePage): boolean => {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        return (
            page?.order !==
            selectedFlow.pages.find(p => p.page_id === page.page_id)?.order
        );
    };
}
